import { useEffect, useState } from 'react';
import { SettingValues } from 'common/services/settings.service';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING } from 'common/constants/constants';
import useSettingsService from 'components/hooks/useSettingsService';
import useReleasesCount from 'views/root/views/home/hooks/useReleasesCount';
import { DefinitionLevels } from '@linearb/linjs-common';
import { useCreateConfigurationFromFilters } from './useCreateConfigurationFromFilters';
import { useIncidentsConfig } from './useIncidentsConfig';
import { usePMConfigPMData } from './usePMConfigPMData';
import { useInitSelectedFilters } from './useInitSelectedFilters';
import { useSavePMConfig } from './useSavePMConfig';

const SINCE_X_DAYS = 30;

export const PM_METRICS_DEFINITION = {
  CFR: {
    settings_value: SettingValues.CFR_CONFIG,
    page_tab: TRACKING.PAGE_TABS.CFR,
    default_incident_config: { value: null }
  },
  MTTR: {
    settings_value: SettingValues.MTTR_CONFIG,
    page_tab: TRACKING.PAGE_TABS.MTTR,
    default_incident_config: { from: null, to: null }
  }
};

const usePMConfig = ({
  toggleSnackBar,
  PMMetric = TRACKING.PAGE_TABS.MTTR,
  teamId,
  setConfiguration,
  parentConfig,
  isUsingParentConfig,
  parentConfiguration
}) => {
  const { getSettingOrDefinitionValue } = useSettingsService();
  const settingConfig = PM_METRICS_DEFINITION[PMMetric].settings_value;
  const pageTab = PM_METRICS_DEFINITION[PMMetric].page_tab;

  const PMConfig = getSettingOrDefinitionValue({
    name: settingConfig,
    definitionLevel: teamId
      ? DefinitionLevels.TEAM
      : DefinitionLevels.ORGANIZATION,
    ...(teamId ? { teamId } : {})
  });

  const [selectedFilters, setSelectedFilters] = useState([]);

  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [gotResponse, setGotResponse] = useState(false);

  const updateFiltersAtIndex = (idx, newItem) => {
    setSelectedFilters(prevFilters => {
      const updatedFilters = [...prevFilters];
      updatedFilters[idx] = newItem;
      return updatedFilters;
    });
  };

  const addCondition = newFilters => {
    setSelectedFilters([...selectedFilters, newFilters]);
  };

  const removeCondition = idx => {
    const filteredArr = [
      ...selectedFilters.slice(0, idx),
      ...selectedFilters.slice(idx + 1)
    ];
    setSelectedFilters(filteredArr);
  };

  const {
    issueTypes,
    statuses,
    formattedFields,
    formattedLabels,
    formattedDateTimeFields,
    formattedIssueTypes
  } = usePMConfigPMData({ setIsLoadingFilters, setGotResponse });

  const releasesCount = useReleasesCount({ sinceDaysCounter: SINCE_X_DAYS });

  useEffect(() => {
    TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
      page: TRACKING.PAGES.SETTINGS,
      page_tab: pageTab
    });
  }, []);

  const {
    initIncidentsConfig,
    incidentConfig,
    setIncidentConfig,
    incidentStrategy,
    setIncidentStrategy,
    shouldAddIncidentToConfig,
    validateincidentConfig
  } = useIncidentsConfig({
    PMConfig,
    PMMetric,
    parentConfiguration,
    isUsingParentConfig
  });

  const { initSelectedFilters } = useInitSelectedFilters({
    PMConfig,
    issueTypes
  });

  const { createConfigurationFromSelectedFilters } =
    useCreateConfigurationFromFilters({
      selectedFilters,
      incidentStrategy,
      incidentConfig,
      shouldAddIncidentToConfig
    });

  const { saveSettings, isSaveEnabled, setIsSaveEnabled } = useSavePMConfig({
    selectedFilters,
    incidentStrategy,
    incidentConfig,
    shouldAddIncidentToConfig,
    PMMetric,
    toggleSnackBar
  });

  useEffect(() => {
    if (!gotResponse) return;

    setConfiguration(PMConfig);

    const initFormattedSelectedFilters = () => {
      const { incidentConfig, incidentStrategy } = initIncidentsConfig();
      const initializedSelectedFilters = initSelectedFilters(PMConfig);
      const initializedParentSelectedFilters =
        initSelectedFilters(parentConfiguration);
      setSelectedFilters(
        isUsingParentConfig
          ? initializedParentSelectedFilters
          : initializedSelectedFilters
      );
      setIncidentStrategy(incidentStrategy);
      setIncidentConfig(incidentConfig);
      setIsLoadingFilters(false);
    };
    initFormattedSelectedFilters();
  }, [gotResponse, parentConfig, isUsingParentConfig]);

  useEffect(() => {
    if (!validateincidentConfig(PMMetric, incidentConfig, incidentStrategy)) {
      setIsSaveEnabled(false);
    } else {
      setIsSaveEnabled(true);
    }
  }, [incidentStrategy, incidentConfig]);

  useEffect(() => {
    const newConfig = createConfigurationFromSelectedFilters();
    setConfiguration(newConfig);
  }, [selectedFilters, incidentConfig, incidentStrategy]);

  return {
    issueTypes: formattedIssueTypes,
    labels: formattedLabels,
    customFields: formattedFields,
    dateTimeCustomFields: formattedDateTimeFields,
    statuses: statuses.map(({ id, name }) => ({ id, title: name })),
    saveSettings,
    selectedFilters,
    setSelectedFilters,
    isLoadingFilters,
    releasesCount,
    incidentStrategy,
    setIncidentStrategy,
    incidentConfig,
    setIncidentConfig,
    isSaveEnabled,
    updateFiltersAtIndex,
    gotResponse,
    addCondition,
    removeCondition
  };
};

export default usePMConfig;
